import {
  AntdList,
  Button,
  Descriptions,
  Divider,
  Icons,
  Modal,
  Spin,
  Table,
  Tabs,
  TextField,
} from '@pankod/refine-antd'
import { useInvalidate, useOne } from '@pankod/refine-core'
import { useMemo } from 'react'

import {
  PaymentProof,
  PeriodePenilaian,
  SyncRecording,
  Timespend,
  TimespendDetail,
} from '../types'
import { secondToMinuteText } from '@resources/lms-management/program/utils'
import {
  getEligibilityTag,
  getEligibilityTagByDuration,
  getPaymentStatusTag,
} from '../utils'
import UploadPaymentProofModal, {
  useUploadPaymentProofModal,
} from './UploadPaymentProofModal'

const getAsyncTotalDuration = (async?: TimespendDetail['async']) => {
  return async?.reduce((result, current) => result + current.duration, 0) || 0
}

const getSyncTotalDuration = (sync?: TimespendDetail['sync']) => {
  return (
    sync?.reduce(
      (result, current) =>
        result +
        current.recordings.reduce(
          (recordingTotal, currentRecording) =>
            recordingTotal + currentRecording.duration,
          0,
        ),
      0,
    ) || 0
  )
}

type ModalTimespendDetailProps = {
  visible: boolean
  onCancel: () => void
  userId?: string
  kelasId?: string
  programId?: any
  localProgramId?: number
}

export const ModalTimespendDetail = ({
  visible,
  onCancel,
  userId,
  kelasId,
  localProgramId,
  programId,
}: ModalTimespendDetailProps) => {
  const invalidate = useInvalidate()
  const { show, ...uploadPaymentProofProps } = useUploadPaymentProofModal()
  const { data: timespendDetail, isLoading } = useOne<{
    data: Timespend
  }>({
    resource: `kelas/${kelasId}/timespends`,
    id: (userId as string) || '',
    dataProviderName: 'lms',
    queryOptions: {
      enabled: !!userId && !!kelasId && visible,
    },
  })

  const totalDuration = useMemo(
    () =>
      timespendDetail?.data?.data?.periode?.reduce(
        (result, { async, sync }) => {
          const asyncTotalDuration = getAsyncTotalDuration(async)
          const syncTotalDuration = getSyncTotalDuration(sync)
          return {
            asyncTotalDuration: result.asyncTotalDuration + asyncTotalDuration,
            syncTotalDuration: result.syncTotalDuration + syncTotalDuration,
          }
        },
        { asyncTotalDuration: 0, syncTotalDuration: 0 },
      ) || { asyncTotalDuration: 0, syncTotalDuration: 0 },
    [timespendDetail?.data?.data?.periode],
  )

  const infoData = timespendDetail?.data?.data?.info

  const getUploadPaymentProofData = () => {
    const data = timespendDetail?.data?.data?.periode?.find(
      ({ id: periodeId }) => periodeId === uploadPaymentProofProps.periodeId,
    )

    return {
      imgUrl: data?.paymentProof?.imageUrl,
      amount: data?.paymentProof?.amount,
      date: data?.paymentProof?.paymentDate,
      notes: data?.paymentProof?.notes,
      buktiBayarId: data?.paymentProof?.id,
    }
  }

  return (
    <Modal
      title="Detail Timespend dan Pembayaran"
      visible={visible}
      onCancel={onCancel}
      width={'80%'}
      footer={null}
    >
      <Spin spinning={isLoading}>
        <Descriptions
          column={1}
          title="Informasi Fasilitator"
          bordered
          className="mb-6"
          size="small"
        >
          <Descriptions.Item label="Nama Fasilitator">
            {infoData?.fasilitatorName}
          </Descriptions.Item>
          <Descriptions.Item label="Kelas">
            {infoData?.kelasName}
          </Descriptions.Item>
        </Descriptions>

        <Descriptions
          title="Ringkasan Timespend Seluruh Periode"
          bordered
          column={1}
          className="mb-6"
          size="small"
        >
          <Descriptions.Item label="Total Durasi Async">
            {secondToMinuteText(totalDuration.asyncTotalDuration)}
          </Descriptions.Item>

          <Descriptions.Item label="Total Durasi Sync">
            {secondToMinuteText(totalDuration.syncTotalDuration)}
          </Descriptions.Item>

          <Descriptions.Item label="Total Durasi Async & Sync">
            {secondToMinuteText(
              totalDuration.asyncTotalDuration +
                totalDuration.syncTotalDuration,
            )}
          </Descriptions.Item>
        </Descriptions>

        <Divider className="!mb-6">Detail Periode & Pembayaran</Divider>

        <Table<
          TimespendDetail & PeriodePenilaian & { paymentProof: PaymentProof }
        >
          dataSource={timespendDetail?.data?.data?.periode}
          rowKey="id"
          bordered
          pagination={false}
          scroll={{ x: 900 }}
          expandable={{
            expandedRowRender: ({
              async,
              sync,
              asyncTargetDuration,
              syncTargetDuration,
            }) => (
              <Tabs type="card">
                <Tabs.TabPane tab="Asynchronous" key="async">
                  <Table
                    tableLayout="fixed"
                    dataSource={async}
                    bordered
                    summary={() => (
                      <>
                        <Table.Summary.Row>
                          <Table.Summary.Cell index={0}>
                            <TextField value={'Ringkasan'} strong />
                          </Table.Summary.Cell>
                          <Table.Summary.Cell index={1} align="center">
                            {secondToMinuteText(getAsyncTotalDuration(async))}
                          </Table.Summary.Cell>
                          <Table.Summary.Cell index={2} align="center">
                            {secondToMinuteText(asyncTargetDuration)}
                          </Table.Summary.Cell>
                          <Table.Summary.Cell index={3} align="center">
                            {getEligibilityTagByDuration(
                              getAsyncTotalDuration(async),
                              asyncTargetDuration,
                            )}
                          </Table.Summary.Cell>
                        </Table.Summary.Row>
                      </>
                    )}
                    columns={[
                      {
                        title: 'Modul',
                        dataIndex: 'name',
                        key: 'name',
                      },
                      {
                        title: 'Durasi',
                        dataIndex: 'duration',
                        key: 'duration',
                        render: (value: number) => secondToMinuteText(value),
                      },
                      {
                        title: 'Target',
                        onCell: (_, i) => ({
                          rowSpan: i === 0 ? async.length : 0,
                        }),
                        align: 'center',
                      },
                      {
                        title: 'Status',
                        onCell: (_, i) => ({
                          rowSpan: i === 0 ? async.length : 0,
                        }),
                        align: 'center',
                      },
                    ]}
                    pagination={false}
                    size="small"
                  />
                </Tabs.TabPane>
                <Tabs.TabPane tab="Synchronous" key="sync">
                  <Table
                    tableLayout="fixed"
                    dataSource={sync}
                    bordered
                    summary={() => (
                      <>
                        <Table.Summary.Row>
                          <Table.Summary.Cell index={0} colSpan={2}>
                            <TextField value={'Ringkasan'} strong />
                          </Table.Summary.Cell>
                          <Table.Summary.Cell index={1} align="center">
                            {secondToMinuteText(getSyncTotalDuration(sync))}
                          </Table.Summary.Cell>
                          <Table.Summary.Cell index={2} align="center">
                            {secondToMinuteText(syncTargetDuration)}
                          </Table.Summary.Cell>
                          <Table.Summary.Cell index={3} align="center">
                            {getEligibilityTagByDuration(
                              getSyncTotalDuration(sync),
                              syncTargetDuration,
                            )}
                          </Table.Summary.Cell>
                        </Table.Summary.Row>
                      </>
                    )}
                    columns={[
                      {
                        title: 'Modul',
                        dataIndex: 'name',
                        key: 'name',
                      },
                      {
                        title: 'Link Rekaman',
                        dataIndex: 'recordings',
                        key: 'recordings',
                        render: (value: SyncRecording[]) => (
                          <AntdList
                            dataSource={value}
                            size="small"
                            renderItem={(item, i) => (
                              <AntdList.Item key={i} className="h-[49px] !pl-0">
                                <Button
                                  type="link"
                                  icon={<Icons.LinkOutlined />}
                                  href={item.url}
                                  target="_blank"
                                  rel="noreferrer noopener"
                                  block
                                  className="!text-left !pl-0"
                                  title={item.title}
                                  size="small"
                                >
                                  <TextField
                                    value={item.title}
                                    ellipsis
                                    className="!text-inherit"
                                  />
                                </Button>
                              </AntdList.Item>
                            )}
                          />
                        ),
                      },
                      {
                        title: 'Durasi',
                        dataIndex: 'recordings',
                        key: 'duration',
                        render: (value: SyncRecording[]) => (
                          <AntdList
                            dataSource={value}
                            size="small"
                            renderItem={(item, i) => (
                              <AntdList.Item key={i} className="h-[49px]">
                                {secondToMinuteText(item.duration)}
                              </AntdList.Item>
                            )}
                          />
                        ),
                      },
                      {
                        title: 'Target',
                        onCell: (_, i) => ({
                          rowSpan: i === 0 ? sync.length : 0,
                        }),
                        align: 'center',
                      },
                      {
                        title: 'Status',
                        onCell: (_, i) => ({
                          rowSpan: i === 0 ? sync.length : 0,
                        }),
                        align: 'center',
                      },
                    ]}
                    pagination={false}
                    size="small"
                  />
                </Tabs.TabPane>
              </Tabs>
            ),
            expandIcon: ({ expanded, onExpand, record }) =>
              expanded ? (
                <Button
                  size="small"
                  type="link"
                  icon={<Icons.UpCircleOutlined />}
                  onClick={(e) => onExpand(record, e)}
                >
                  Detail
                </Button>
              ) : (
                <Button
                  size="small"
                  type="text"
                  icon={<Icons.DownCircleOutlined />}
                  onClick={(e) => onExpand(record, e)}
                >
                  Detail
                </Button>
              ),
          }}
          columns={[
            {
              title: 'Periode',
              dataIndex: 'name',
              width: '200px',
            },
            {
              title: 'Durasi',
              children: [
                {
                  title: 'Asynchronous',
                  dataIndex: 'async',
                  render: (value) =>
                    secondToMinuteText(getAsyncTotalDuration(value)),
                },
                {
                  title: 'Synchronous',
                  dataIndex: 'sync',
                  render: (value) =>
                    secondToMinuteText(getSyncTotalDuration(value)),
                },
              ],
            },
            {
              title: 'Status Pencapaian',
              dataIndex: ['paymentProof', 'eligibleStatus'],
              render: (value) => getEligibilityTag(value),
            },
            {
              title: 'Pembayaran',
              children: [
                {
                  title: 'Status Pembayaran',
                  dataIndex: ['paymentProof', 'paymentStatus'],
                  render: (value) => getPaymentStatusTag(value),
                },
                {
                  title: 'Bukti Pembayaran',
                  dataIndex: ['paymentProof'],
                  render: (_, record) => {
                    const isPaid =
                      record.paymentProof?.paymentStatus ===
                      'PAYMENT_STATUS_PAID'
                    return (
                      <Button
                        block
                        icon={
                          isPaid ? (
                            <Icons.EyeOutlined />
                          ) : (
                            <Icons.UploadOutlined />
                          )
                        }
                        onClick={() =>
                          show({
                            periodeId: record.id!,
                            kelasId: kelasId!,
                            name: infoData?.fasilitatorName!,
                          })
                        }
                      >
                        {isPaid ? 'Lihat' : 'Unggah'}
                      </Button>
                    )
                  },
                },
              ],
            },
          ]}
        />
      </Spin>
      <UploadPaymentProofModal
        {...uploadPaymentProofProps}
        {...getUploadPaymentProofData()}
        userId={userId!}
        onSuccess={() => {
          invalidate({
            invalidates: ['list'],
            dataProviderName: 'lms',
            resource: `programs/${programId}/local-programs/${localProgramId}/timespends`,
          })
          invalidate({
            id: userId,
            invalidates: ['detail'],
            dataProviderName: 'lms',
            resource: `kelas/${kelasId}/timespends`,
          })
        }}
      />
    </Modal>
  )
}
